import { useEffect, useState } from "react"
import { useMutation } from "react-relay"
import {
  AddressInput,
  chCheckPartnerPortalEligibilityMutation as ChCheckPartnerPortalEligibilityMutationType,
} from "@/modules/patients/detail/progress-note/point-of-care/services/__generated__/chCheckPartnerPortalEligibilityMutation.graphql"
import { chCheckPartnerPortalEligibilityMutation } from "@/modules/patients/detail/progress-note/point-of-care/services/chCheckPartnerPortalEligibilityMutation"

type UseCheckPartnerPortalEligibilityParameters = {
  patientId: string
  address: AddressInput | null
  enabled: boolean
}

export type ClosestCareProvider = NonNullable<
  NonNullable<
    ChCheckPartnerPortalEligibilityMutationType["response"]["ch_checkPartnerPortalEligibility"]["checkPartnerPortalEligibilityOutput"]
  >["closestCareProvider"]
>

type PartnerPortalEligibilityResult = {
  isEligible: boolean
  nearbyCareProviderIds: ReadonlyArray<number>
  closestCareProvider: ClosestCareProvider | null
  isLoading: boolean
  error: string | null
}

export const useCheckPartnerPortalEligibility = ({
  patientId,
  address,
  enabled,
}: UseCheckPartnerPortalEligibilityParameters): PartnerPortalEligibilityResult => {
  const [isEligible, setIsEligible] = useState(false)
  const [nearbyCareProviderIds, setNearbyCareProviderIds] = useState<ReadonlyArray<number>>([])
  const [closestCareProvider, setClosestCareProvider] = useState<ClosestCareProvider | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [commit, isLoading] = useMutation<ChCheckPartnerPortalEligibilityMutationType>(
    chCheckPartnerPortalEligibilityMutation,
  )

  useEffect(() => {
    if (!enabled || !address || !patientId) {
      return
    }

    const resetToNotEligible = (message: string) => {
      setIsEligible(false)
      setNearbyCareProviderIds([])
      setClosestCareProvider(null)
      setError(message)
    }

    commit({
      variables: {
        input: {
          patientId,
          address,
        },
      },
      onCompleted: (response) => {
        const { checkPartnerPortalEligibilityOutput: output, errors } = response.ch_checkPartnerPortalEligibility

        if (errors && errors.length > 0) {
          resetToNotEligible(errors[0].message ?? "Unknown error")
          return
        }

        if (output) {
          setIsEligible(output.isEligible)
          setNearbyCareProviderIds(output.nearbyCareProviderIds)
          setClosestCareProvider(output.closestCareProvider ?? null)
          setError(null)
        }
      },
      onError: (mutationError) => resetToNotEligible(mutationError.message),
    })
  }, [commit, enabled, address, patientId])

  return {
    isEligible,
    nearbyCareProviderIds,
    closestCareProvider,
    isLoading,
    error,
  }
}
